import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { ToastrService } from 'ngx-toastr';
import { ClientEncryptionPasswordService } from 'src/app/service/client-encryption-password.service';
import { IndexDBService } from 'src/app/service/indexDB/index-db.service';
import { PasswordType, UserProfileService } from 'src/app/service/user-profile.service';
import SecurityUtils from '../../Utility/securityUtils';
import PasswordStrength, { PasswordStrengthLevel } from '../../Utility/passwordStrength';
import { scrollDeltaToReveal } from '../../Utility/revealInScroller';

const MAX_FIRST_RENDER_NUDGE_PX = 48;
export function confirmPasswordValidator(
  passwordField: string = 'password',
  confirmPasswordField: string = 'confirmPassword'
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const control = formGroup.get(passwordField);
    const matchingControl = formGroup.get(confirmPasswordField);
    if (control?.value !== matchingControl?.value) {
      matchingControl?.setErrors({ confirmedValidator: true });
    } else {
      matchingControl?.setErrors(null);
    }
    return null;
  };
}
@Component({
  selector: 'app-encryption-password',
  templateUrl: './encryption-password.component.html',
  styleUrls: ['./encryption-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EncryptionPasswordComponent {
  public readonly pinMask = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
  public hidePassword = true;
  public readonly animationSettings: Object = {
    effect: 'Zoom',
    duration: 600,
  };
  isVisible = false;
  allowClosing = false;
  passwordControl = new FormControl('', [Validators.required,  Validators.minLength(8)]);
  setupPassword = new FormGroup(
    {
      password: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(8),
      ]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    [confirmPasswordValidator('password', 'confirmPassword')]
  );
  isPasswordSetup = false;
  type: PasswordType = 'Password';
  state: 'FillPassword' | 'DeleteConfirmation' = 'FillPassword';
  selectedIndex = 0;
  showError = false;
  numberOffline: {notebooks: number; notes: number} = null;

  // ===========================================================================
  // Password strength (Weak / Fair / Strong)
  //
  // Advisory only - it never blocks Save. The existing minLength(8) validator
  // is still the only gate, so this tells the user how good their choice is
  // without deciding for them.
  // ===========================================================================
  public strengthLevel: PasswordStrengthLevel = 'weak';
  public strengthLabel: string = '';
  public strengthPercent: number = 0;
  public strengthAdvice: string = '';

  public onPasswordInput(value: string) {
    // The user's own email/name are the first things an attacker targeting
    // THEM would try, so they are scored as forbidden inputs.
    const profile: any = this.userProfileService?.user || {};
    const userInputs: string[] = [profile.usersEmail, profile.legalName, profile.displayName].filter((v) => !!v);

    const result = PasswordStrength.evaluate(value || '', userInputs);

    this.strengthLevel = result.level;
    this.strengthLabel = result.label;
    this.strengthPercent = result.percent;
    this.strengthAdvice = result.advice;

    this.cd.detectChanges();
  }
  constructor(
    private clientEncryptionPasswordService: ClientEncryptionPasswordService,
    private indexDBService: IndexDBService,
    private cd: ChangeDetectorRef,
    private toastr: ToastrService,
    private userProfileService: UserProfileService,
    private securityUtils: SecurityUtils,
  ) {
   
  }

  // ===================================
  // SHOW
  // ===================================
  async show() {
    this.isVisible = true;
    this.hidePassword = true;
    this.allowClosing = false;
    this.setupData();
    this.cd.detectChanges();
  }

  // ===================================
  // HIDE
  // ===================================
  hide() {
    this.isVisible = false;
    this.allowClosing = true;
    this.cd.detectChanges();
  }

  validation(event: any) {
    if (!this.allowClosing) {
      event.cancel = true;
    }
  }

  onTypeChange(event: MatRadioChange) {
    const { value } = event;
    this.type = value;
    this.setupFormAfterType();
    this.cd.detectChanges();
  }

  // Changing PIN step removes the old step before the new one renders, and
  // the dialog body's scroll snapped to the top (the keypad and Save ended up
  // below the fold). Put the scroll back on the next frame, once the new
  // step is in the DOM, then bring its cells, keypad and button fully into
  // view.
  //
  // When the PIN tabs first appear nothing is scrolled, so the option cards
  // stay in view, except a nudge of up to 48px when Confirm is already on
  // screen but the tap area under it is cut off by the dialog edge (440x956).
  // Typing never scrolls.
  @ViewChild('pinStep') pinStep?: ElementRef<HTMLElement>;
  @ViewChild('pinTabs', { read: ElementRef }) pinTabs?: ElementRef<HTMLElement>;
  private pendingReveal = false;
  private firstRenderSeen?: HTMLElement;

  keepPinScroll() {
    this.pendingReveal = true;
    const scroller = this.pinTabs?.nativeElement.closest('.e-dlg-content');
    const scrollTop = scroller?.scrollTop ?? 0;
    requestAnimationFrame(() => {
      if (scroller) {
        scroller.scrollTop = scrollTop;
      }
      this.revealPinStep();
    });
  }

  revealPinStep() {
    const tabs = this.pinTabs?.nativeElement;
    const isFirstRender = !!tabs && tabs !== this.firstRenderSeen;
    this.firstRenderSeen = tabs;
    const afterStepChange = this.pendingReveal;
    this.pendingReveal = false;
    const step = this.pinStep?.nativeElement;
    const scroller = step?.closest('.e-dlg-content');
    if (!step || !scroller || !(afterStepChange || isFirstRender)) {
      return;
    }
    const delta = scrollDeltaToReveal(step.getBoundingClientRect(), scroller.getBoundingClientRect());
    if (afterStepChange || delta <= MAX_FIRST_RENDER_NUDGE_PX) {
      scroller.scrollTop += delta;
    }
  }

  onBack() {
    this.selectedIndex = 0;
    this.setupPassword.reset();
    this.showError = false;
    this.cd.detectChanges();
  }

  onChangeNumpadSetupPassword(password: string, control: string) {
    this.setupPassword.get(control).setValue(password);
    this.cd.detectChanges();
  }

  onChangeNumpad(password: string) {
    this.passwordControl.setValue(password)
    this.cd.detectChanges();
  }

  onSetupPIN() {
    if(this.setupPassword.invalid) {
      this.showError = true;
      return;
    }
    this.showError = false;
    this.onSetupPassword();
  }

  async onSetupPassword() {
    const { password } = this.setupPassword.getRawValue();
    this.setupPassword.reset();
    await this.clientEncryptionPasswordService.setupCustomKEK(password, this.type);
    this.hide();
    this.toastr.success('Password has been setup successfully');
    this.cd.detectChanges();
  }

  async onSavePassword() {
    const password = this.passwordControl.getRawValue();
    this.passwordControl.reset();
    const success = await this.clientEncryptionPasswordService.setCustomKEK(password);
    if (success) {
      this.hide();
      const successMessage = this.isPasswordSetup ? 'Password Confirmed' : 'Password has been setup successfully';
      this.toastr.success(successMessage);
      this.cd.detectChanges();
      return;
    }
    this.state = 'DeleteConfirmation';
    this.cd.detectChanges();
  }

  onDeleteCancel() {
    this.state = 'FillPassword';
    this.cd.detectChanges();
  }

  onDeleteOldEncriptionPassword() {
    this.clientEncryptionPasswordService.clearCustomKEK();
    this.indexDBService.DeleteAllDatabases();
    this.toastr.success('Client Data Deleted');
    this.state = 'FillPassword';
    this.setupData();
    this.cd.detectChanges();
  }
  

  decorateMessageError(error: any, ...arg) {
    const messages: any = {
      required: 'Field is required',
      lowercase: 'Password must contain a lower case letter',
      uppercase: 'Password must contain an upper case letter',
      number: 'Password must contain a number',
      minlength: (error) => `Password must contain at least ${error.requiredLength} Characters`,
      special: 'Password must contain a special character',
      confirmedValidator: 'Passwords do not match',
      email: 'Invalid email address',
      emailValidator: 'Please double check your email address',
    };
    const key = Object.keys(error).find((key) => !!error[key]);
    if (!key) {
      return '';
    }
    if(key === 'minlength') {
      return messages['minlength'](error['minlength']);
    }
    const message = messages[key];
    return message;
  }

  private async setupData() {
    this.isPasswordSetup = !!(this.userProfileService.isCachedUser() && this.securityUtils.isServiceWorkerCacheEnabled());
    if (this.isPasswordSetup) {
      this.type = this.userProfileService.getPasswordType();
    }else {
      this.type = 'Password';
    }
    this.setupFormAfterType();
   
    this.numberOffline = await this.indexDBService.getNumberOfflineNotes();
    this.cd.detectChanges();
  }

  private setupFormAfterType() {
    this.setupPassword.reset();
    this.selectedIndex = 0;
    this.showError = false;
    if (this.type === 'PIN') {
      this.setupPassword.get('password').setValidators([Validators.required, Validators.minLength(8)]);
    }
    if (this.type === 'Password') {
      this.setupPassword.get('password').setValidators([Validators.required, Validators.minLength(8)]);
    }
  }
}
